import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CommentOutlined,
  PrinterOutlined,
  SaveOutlined,
  SendOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import orderReviewChecklistService, { type OrderReviewChecklist } from '@/services/orderReviewChecklist.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { approvalService } from '@/services/approval.service';
import { ENTITY_TYPE_ORDER_REVIEW_CHECKLIST } from '@/constants/approval-entity-types';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { useOpportunity } from '@/hooks/useOpportunities';
import { openEngineReportPrint } from '@/components/print/openEngineReportPrint';
import {
  formatOrcSectionTitle,
  getDefaultOrcItemLabel,
  getReviewOfferFromSections,
  isProductInfoSection,
  isReviewOfferItem,
} from '@/constants/order-review-checklist-sections';

const { Text } = Typography;

const RESPONSE_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'N/A', label: 'Not Applicable' },
];

function isCompleteResponse(response: unknown): boolean {
  const r = String(response ?? '')
    .trim()
    .toLowerCase();
  return r === 'yes' || r === 'n/a' || r === 'na';
}

function isProductInfoComplete(item: { remarks?: unknown } | undefined): boolean {
  return Boolean(String(item?.remarks ?? '').trim());
}

function sectionProgress(sectionKey: string, items: unknown): { complete: number; total: number } {
  const list = Array.isArray(items) ? items : [];
  if (isProductInfoSection(sectionKey)) {
    return {
      total: list.length,
      complete: list.filter((item) => isProductInfoComplete(item as { remarks?: unknown })).length,
    };
  }
  return {
    total: list.length,
    complete: list.filter((item) => isCompleteResponse((item as { response?: unknown })?.response)).length,
  };
}

const ChecklistRemarksCell: React.FC<{
  name: Array<string | number>;
  editable: boolean;
  placeholder?: string;
  rows?: number;
}> = ({ name, editable, placeholder = 'Remark', rows }) => {
  const remarks = Form.useWatch(name);
  const [open, setOpen] = useState(false);
  const hasText = Boolean(String(remarks || '').trim());
  const show = open || hasText || Boolean(rows && rows > 1);

  if (!show) {
    return (
      <Tooltip title="Add remark">
        <Button
          type="text"
          size="small"
          disabled={!editable}
          icon={<CommentOutlined />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
    );
  }

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Form.Item name={name} style={{ marginBottom: 0, flex: 1 }}>
        {rows && rows > 1 ? (
          <Input.TextArea
            size="small"
            rows={rows}
            placeholder={placeholder}
            disabled={!editable}
            onBlur={(e) => {
              if (!String(e.target.value || '').trim() && !rows) setOpen(false);
            }}
          />
        ) : (
          <Input
            size="small"
            placeholder={placeholder}
            disabled={!editable}
            onBlur={(e) => {
              if (!String(e.target.value || '').trim()) setOpen(false);
            }}
          />
        )}
      </Form.Item>
    </Space.Compact>
  );
};

const CollapsibleNotesField: React.FC<{
  name: string;
  label: string;
  editable: boolean;
}> = ({ name, label, editable }) => {
  const value = Form.useWatch(name);
  const [open, setOpen] = useState(false);
  const hasText = Boolean(String(value || '').trim());
  const show = open || hasText;

  if (!show) {
    return (
      <Button type="link" size="small" disabled={!editable} onClick={() => setOpen(true)} style={{ paddingLeft: 0 }}>
        Add {label.toLowerCase()}
      </Button>
    );
  }

  return (
    <Form.Item name={name} label={label} style={{ marginBottom: 8 }}>
      <Input.TextArea
        rows={2}
        disabled={!editable}
        onBlur={(e) => {
          if (!String(e.target.value || '').trim()) setOpen(false);
        }}
      />
    </Form.Item>
  );
};

function getSections(row: OrderReviewChecklist | undefined): Record<string, any[]> {
  const raw = row?.sections;
  if (!raw || typeof raw !== 'object') return {};
  return raw as Record<string, any[]>;
}

function getDefaultItemLabel(sectionKey: string, index: number): string {
  return getDefaultOrcItemLabel(sectionKey, index);
}

function mergeSectionsPreservingLabels(
  currentSections: Record<string, any[]>,
  nextSections: Record<string, any[]>
): Record<string, any[]> {
  const keys = new Set<string>([...Object.keys(currentSections || {}), ...Object.keys(nextSections || {})]);
  const merged: Record<string, any[]> = {};
  keys.forEach((key) => {
    const current = Array.isArray(currentSections?.[key]) ? currentSections[key] : [];
    const next = Array.isArray(nextSections?.[key]) ? nextSections[key] : [];
    const max = Math.max(current.length, next.length);
    merged[key] = Array.from({ length: max }, (_, idx) => {
      const c = current[idx] || {};
      const n = next[idx] || {};
      const label = c.label || n.label || getDefaultItemLabel(key, idx);
      return {
        ...c,
        ...n,
        label,
      };
    });
  });
  return merged;
}

const OrderReviewChecklistDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [transferOpen, setTransferOpen] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ['order-review-checklist', id],
    queryFn: () => orderReviewChecklistService.get(id!),
    enabled: !!id,
  });
  const { data: approvalSummary } = useQuery({
    queryKey: ['approval-summary', ENTITY_TYPE_ORDER_REVIEW_CHECKLIST, id],
    queryFn: () => approvalService.getSummary(ENTITY_TYPE_ORDER_REVIEW_CHECKLIST, id!),
    enabled: !!id && !!row?.approval_instance_id,
  });
  const { data: linkedOpportunity } = useOpportunity(row?.opportunity_id || undefined);
  const opportunityLabel = linkedOpportunity
    ? `${linkedOpportunity.opportunity_number} — ${linkedOpportunity.opportunity_name}`
    : '—';

  useEffect(() => {
    if (!row) return;
    form.setFieldsValue({
      review_date: row.review_date ? dayjs(row.review_date) : dayjs(),
      rfq_reference: row.rfq_reference || undefined,
      priority: row.priority || undefined,
      remarks: row.remarks || undefined,
      internal_notes: row.internal_notes || undefined,
      sections: getSections(row),
    });
  }, [row, form]);

  const editable = useMemo(() => {
    if (!row) return false;
    return row.review_status === 'Draft' || row.review_status === 'Returned for Correction';
  }, [row]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const v = await form.validateFields();
      const currentSections = getSections(row);
      const nextSections = (v.sections || {}) as Record<string, any[]>;
      return orderReviewChecklistService.update(id!, {
        review_date: v.review_date ? v.review_date.format('YYYY-MM-DD') : undefined,
        rfq_reference: v.rfq_reference,
        priority: v.priority,
        remarks: v.remarks,
        internal_notes: v.internal_notes,
        sections: mergeSectionsPreservingLabels(currentSections, nextSections),
      });
    },
    onSuccess: () => {
      message.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['order-review-checklist', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const submitMutation = useMutation({
    mutationFn: () => orderReviewChecklistService.submit(id!),
    onSuccess: () => {
      message.success('Submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['order-review-checklist', id] });
      queryClient.invalidateQueries({ queryKey: ['approval-summary', ENTITY_TYPE_ORDER_REVIEW_CHECKLIST, id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => orderReviewChecklistService.cancel(id!),
    onSuccess: () => {
      message.success('Checklist cancelled');
      queryClient.invalidateQueries({ queryKey: ['order-review-checklist', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Cancel failed'),
  });

  const watchedSections = Form.useWatch('sections', form);

  const onPrint = () => {
    if (!row) return;
    const sections = getSections(row);
    const lines: Array<{ lineNumber: number; description: string; cells: Record<string, string> }> = [];
    let n = 0;
    for (const [key, items] of Object.entries(sections)) {
      lines.push({
        lineNumber: ++n,
        description: `— ${key.replace(/_/g, ' ')} —`,
        cells: { response: '', remarks: '' },
      });
      if (Array.isArray(items)) {
        items.forEach((x, idx) => {
          lines.push({
            lineNumber: ++n,
            description: x.label || getDefaultItemLabel(key, idx) || '—',
            cells: {
              response: x.response || '',
              remarks: x.remarks || '',
            },
          });
        });
      }
    }
    openEngineReportPrint({
      docType: 'order_review_checklist',
      title: 'ORDER REVIEW CHECKLIST',
      documentNumber: row.orc_number || 'ORC',
      partyName: row.customer_name || '',
      metaLines: [
        { label: 'ORC #', value: row.orc_number || '' },
        { label: 'Opportunity', value: opportunityLabel || '' },
        { label: 'Customer', value: row.customer_name || '' },
        { label: 'Review date', value: row.review_date || '' },
        { label: 'Status', value: row.review_status || '' },
        { label: 'Approval', value: row.approval_status || '' },
      ].filter((m) => m.value),
      columns: [
        { key: 'description', label: 'Item' },
        { key: 'response', label: 'Response' },
        { key: 'remarks', label: 'Remarks' },
      ],
      lines,
      finalNote: (() => {
        const offer = getReviewOfferFromSections(sections);
        if (offer) {
          return `Agreed to send offer: ${offer.response || '—'} | Quote/Proposal ref: ${offer.remarks || '—'} | Remarks: ${row.remarks || '—'}`;
        }
        return `Risk: ${row.overall_risk_rating || '—'} | Recommendation: ${row.final_recommendation || '—'} | Remarks: ${row.remarks || '—'}`;
      })(),
    });
  };

  if (isLoading || !row) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const persistedSections = getSections(row);
  const sectionTabs = Object.entries(persistedSections).map(([key, items]) => {
    const list = Array.isArray(items) ? items : [];
    const liveItems = Array.isArray(watchedSections?.[key]) ? watchedSections[key] : list;
    const progress = sectionProgress(key, liveItems);
    const productInfo = isProductInfoSection(key);
    const columns: ColumnsType<{ key: number; label: string }> = [
      {
        title: 'Item',
        dataIndex: 'label',
        key: 'label',
        render: (label: string) => <Text>{label}</Text>,
      },
      ...(productInfo
        ? []
        : [
            {
              title: 'Response',
              key: 'response',
              width: 320,
              render: (_: unknown, record: { key: number; label: string }) => (
                <Form.Item name={['sections', key, record.key, 'response']} style={{ marginBottom: 0 }}>
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    disabled={!editable}
                    options={RESPONSE_OPTIONS}
                  />
                </Form.Item>
              ),
            } as ColumnsType<{ key: number; label: string }>[number],
          ]),
      {
        title: productInfo ? 'Details' : 'Remarks',
        key: 'remarks',
        width: productInfo ? 420 : 220,
        render: (_, record) => (
          <ChecklistRemarksCell
            name={['sections', key, record.key, 'remarks']}
            editable={editable}
            placeholder={
              isReviewOfferItem(key, record.label)
                ? 'Quote/Proposal number and date'
                : productInfo
                  ? 'Provide information as applicable'
                  : 'Remark'
            }
            rows={productInfo ? 3 : undefined}
          />
        ),
      },
    ];
    return {
      key,
      label: (
        <span>
          {formatOrcSectionTitle(key)}{' '}
          <Tag style={{ marginInlineStart: 4 }}>
            {progress.complete}/{progress.total}
          </Tag>
        </span>
      ),
      children: (
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          columns={columns}
          dataSource={list.map((item, idx) => ({
            key: idx,
            label: item?.label || getDefaultItemLabel(key, idx) || '—',
          }))}
        />
      ),
      forceRender: true,
    };
  });

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/order-review-checklists')}>
              Back
            </Button>
            <h2 style={{ margin: 0 }}>{row.orc_number}</h2>
            <Tag>{row.review_status}</Tag>
            <Tag color="blue">{row.approval_status}</Tag>
            {approvalSummary?.instance?.status === 'pending_approval' ? (
              <PendingApproverText
                compact
                prefix="Next approver"
                pendingApproverLabel={approvalSummary.pendingApproverLabel}
                pendingApprovers={approvalSummary.pendingApprovers}
              />
            ) : null}
            {row.is_stale ? <Tag color="red">Stale</Tag> : null}
          </Space>
          <Space>
            {editable ? (
              <>
                <Button icon={<SaveOutlined />} loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                  Save draft
                </Button>
                <Button type="primary" icon={<SendOutlined />} loading={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                  Submit for approval
                </Button>
                <Button danger loading={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                  Cancel
                </Button>
              </>
            ) : null}
            <Button icon={<PrinterOutlined />} onClick={onPrint}>
              Print
            </Button>
            <ReportDocumentActions
              reportKey="order-review-checklist"
              params={{ id: row.id }}
              documentId={row.id}
              sendPermissions={['crm.order_review_checklist.edit', 'growth.view']}
            />
            <Button onClick={() => navigate(`/growth/opportunities/${row.opportunity_id}`)}>Open opportunity</Button>
            <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
              Transfer team
            </Button>
          </Space>
        </Space>

        <SalesTeamTransferModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          entityType="OrderReviewChecklist"
          entityId={row.id}
          entityLabel={row.orc_number}
          currentTeamName={row.sales_team_name}
          onSuccess={() => queryClient.invalidateQueries()}
        />
        <TransferHistoryPanel entityType="OrderReviewChecklist" entityId={row.id} />

        <Card title="Header" size="small">
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Opportunity">{opportunityLabel}</Descriptions.Item>
            <Descriptions.Item label="Sales team">{row.sales_team_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Customer">{row.customer_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Review date">{row.review_date ? dayjs(row.review_date).format('DD MMM YYYY') : '—'}</Descriptions.Item>
            <Descriptions.Item label="Approved at">{row.approved_at ? dayjs(row.approved_at).format('DD MMM YYYY HH:mm') : '—'}</Descriptions.Item>
          </Descriptions>
        </Card>

        {row.approval_instance_id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_ORDER_REVIEW_CHECKLIST}
            entityId={row.id}
            onApproveViaDocument={async (remarks) => {
              await orderReviewChecklistService.approve(row.id, remarks);
              queryClient.invalidateQueries({ queryKey: ['order-review-checklist', id] });
            }}
            onRejectViaDocument={async (payload) => {
              await orderReviewChecklistService.rejectUniversal(row.id, payload as any);
              queryClient.invalidateQueries({ queryKey: ['order-review-checklist', id] });
            }}
            extraInvalidateQueryKeys={[
              ['order-review-checklist', id],
              ['approval-summary', ENTITY_TYPE_ORDER_REVIEW_CHECKLIST, id],
            ]}
          />
        ) : null}

        <Card size="small" title="Review body">
          <Form form={form} layout="vertical" disabled={!editable}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="review_date" label="Review date" style={{ marginBottom: 8 }}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="rfq_reference" label="RFQ reference" style={{ marginBottom: 8 }}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="priority" label="Priority" style={{ marginBottom: 8 }}>
                  <Select
                    allowClear
                    options={[
                      { value: 'Low', label: 'Low' },
                      { value: 'Medium', label: 'Medium' },
                      { value: 'High', label: 'High' },
                      { value: 'Urgent', label: 'Urgent' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Space wrap>
              <CollapsibleNotesField name="remarks" label="Remarks" editable={editable} />
              <CollapsibleNotesField name="internal_notes" label="Internal notes" editable={editable} />
            </Space>
            <Divider orientation="left">Checklist sections</Divider>
            <Tabs items={sectionTabs} />
          </Form>
        </Card>

        <EntityAttachmentsCard
          entityType="OrderReviewChecklist"
          entityId={row.id}
          title="Attachments"
          readOnly={false}
          queryKeyPrefix={['documents', 'order-review-checklist']}
        />
        <InheritedAttachmentsCard
          trailDomain="sales"
          entityType="OrderReviewChecklist"
          entityId={row.id}
          queryKeyPrefix={['documents', 'order-review-checklist']}
          style={{ marginTop: 0 }}
        />
      </Space>
    </div>
  );
};

export default OrderReviewChecklistDetailPage;

